import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Switch,
  Typography,
} from "@mui/material";
import {
  isAuthenticated,
  updateTeacherStatus,
} from "../../../ApiService/ApiService";
import {
  Person,
  Email,
  Phone,
  Event,
  Wc,
  Adjust,
  LocationOnOutlined,
} from "@mui/icons-material";
import { formatDateOutput } from "../../../Utils/formatDate";
import "../../../Styles/dashboard.css";
import CustomDialog from "../../../Components/CustomDialog";

const headCells = [
  { id: "name", label: "Name", icon: <Person sx={{ marginRight: 1 }} /> },
  { id: "email", label: "Email", icon: <Email sx={{ marginRight: 1 }} /> },
  {
    id: "address",
    label: "Address",
    icon: <LocationOnOutlined sx={{ marginRight: 1 }} />,
  },
  { id: "phone", label: "Phone", icon: <Phone sx={{ marginRight: 1 }} /> },
  {
    id: "doj",
    label: "Date of Joining",
    icon: <Event sx={{ marginRight: 1 }} />,
  },
  { id: "gender", label: "Gender", icon: <Wc sx={{ marginRight: 1 }} /> },
  {
    id: "status",
    label: "Status",
    icon: <Adjust sx={{ marginRight: 1 }} />,
  },
];

function TeacherTable({ teachers, fetchTeachers }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [paginatedData, setPaginatedData] = useState([]);
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    message: "",
    buttons: [],
  });

  useEffect(() => {
    setPaginatedData(teachers?.data);
  }, [teachers]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDialogClose = () => {
    setDialog({ ...dialog, visible: false });
  };

  const handleStatusChange = async (id, currentStatus) => {
    try {
      if (isAuthenticated()) {
        const updatedStatus = !currentStatus;
        const response = await updateTeacherStatus(id, {
          isActive: updatedStatus,
        });
        if (response) {
          if (response?.data?.status === 200) {
            const updatedData = paginatedData.map((teacher) =>
              teacher.id === id
                ? { ...teacher, isActive: updatedStatus }
                : teacher
            );
            setDialog({
              visible: true,
              title: "Success",
              message: "Status updated successfully",
              buttons: [{ name: "OK", onPress: handleDialogClose }],
            });
            setPaginatedData(updatedData);
            fetchTeachers();
          } else {
            const errorMessage =
              response?.data?.errorMessage || "Something went wrong";
            setDialog({
              visible: true,
              title: "Error",
              message: errorMessage,
              buttons: [{ name: "Close", onPress: handleDialogClose }],
            });
          }
        } else {
          setDialog({
            visible: true,
            title: "Error",
            message: "Invalid response from the server",
            buttons: [{ name: "Close", onPress: handleDialogClose }],
          });
        }
      } else {
        setDialog({
          visible: true,
          title: "Unauthorized action",
          message: "Please login and try again",
          buttons: [{ name: "Close", onPress: handleDialogClose }],
        });
      }
    } catch (error) {
      setDialog({
        visible: true,
        title: "Error",
        message: "Something went wrong",
        buttons: [{ name: "Close", onPress: handleDialogClose }],
      });
    }
  };

  const slicedData = paginatedData?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <CustomDialog
        visible={dialog.visible}
        title={dialog.title}
        message={dialog.message}
        buttons={dialog.buttons}
        onClose={handleDialogClose}
      />
      <Box>
        <Paper>
          <TableContainer>
            <Table aria-labelledby="tableTitle">
              <TableHead>
                <TableRow>
                  {headCells.map((headCell) => (
                    <TableCell key={headCell.id} align="left" padding="normal">
                      <Box className="table-head">
                        {headCell.icon}
                        {headCell.label}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {slicedData?.length > 0 ? (
                  slicedData?.map((row) => (
                    <TableRow key={row?.id}>
                      <TableCell
                        component="th"
                        scope="row"
                        className="table-cell"
                      >
                        {`${row?.firstName} ${
                          row?.middleName ? row?.middleName + " " : ""
                        }${row?.lastName}`}
                      </TableCell>
                      <TableCell className="table-cell">{row?.email}</TableCell>
                      <TableCell className="table-cell">{`${row?.street1}${
                        row?.street2 ? ", " + row?.street2 : ""
                      }, ${row?.city}`}</TableCell>
                      <TableCell className="table-cell">{row?.phone}</TableCell>
                      <TableCell className="table-cell">
                        {formatDateOutput(row?.doj)}
                      </TableCell>
                      <TableCell className="table-cell">
                        {row?.gender === 1
                          ? "Male"
                          : row?.gender === 2
                          ? "Female"
                          : "Others"}{" "}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={row?.isActive}
                          color={row?.isActive ? "success" : "default"}
                          onChange={() =>
                            handleStatusChange(row?.id, row?.isActive)
                          }
                          inputProps={{ "aria-label": "controlled" }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="textSecondary">
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {slicedData?.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10]}
              component="div"
              count={paginatedData?.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      </Box>
    </>
  );
}

TeacherTable.propTypes = {
  teachers: PropTypes.object.isRequired,
  fetchTeachers: PropTypes.func.isRequired,
};

export default TeacherTable;
